import React, { useState, useMemo, useCallback, useRef} from 'react';
import {
  Tooltip,
  Input,
  DatePicker,
  Button,
  Icon,
  Tag,
  Switch,
  Table,
  AutoComplete,
  Select,
  InputNumber,
  Checkbox,
} from 'antd';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import ReactQuill from 'react-quill';
import CustomToolBar, { modules } from "./CustomToolBar";
import {
  ListGridContainer,
  VariableDetail,
  ModalTagContainer,
  TableContainer,
  VariableDataContainer,
  ColumnHeaderErrorIcon,
  RichTextEditorContainer,
} from './styles';
import {
  EmptyListContainer,
  EmptyListImage,
  EmptyListHelpText,
} from '../../../artboard/VariablesList/styles';
import EmptyList from '../../../images/empty-list.svg';
import { ChartColorCell } from '../CreativeBlockDesign/Charts/ChartSettingsSidebar/chartColorCell';
import { useCheckTableInFocus } from '../CreativeBlockDesign/Charts/ChartSettingsSidebar/chartSidebarHelper';
import { EditableCell } from '../CreativeBlockDesign/Charts/ChartSettingsSidebar/editableCell';
import ChangeVariableValue from './changeVariableValue';
import {
  VARIABLE_OBJECT_TYPES,
  VARIABLE_PRIMITIVE_TYPES,
  VARIABLE_TYPE,
} from '../../../utils/constants/appConstants';
import { deepCopy } from '../../../utils/javascript';
import { TEMPLATE_VARIABLES_SIDEBAR } from '../../../artboard/VariablesList/translationValues';
import { COMPLEX_VARIABLES_SIDEBAR } from './translationValues';
import { QuillDeltaToHtmlConverter } from 'quill-delta-to-html';

const Option = Select.Option;

export const ErrorLabel = ({ errorMessages, variable, t, isObject = false }) => {
  let errorMessage = '';
  const name = `${isObject ? `${variable.key}/` : ''}${variable.placeHolderText || variable.name}`;
  if (errorMessages?.[name]?.chartValue) {
    errorMessage = t(
      TEMPLATE_VARIABLES_SIDEBAR.CHART_VALUE_ERROR_MESSAGE.key,
      TEMPLATE_VARIABLES_SIDEBAR.CHART_VALUE_ERROR_MESSAGE.value,
    );
  } else if (errorMessages?.[name]?.barcode39) {
    errorMessage = t(
      TEMPLATE_VARIABLES_SIDEBAR.BARCODE39_VALUE_ERROR_MESSAGE.key,
      TEMPLATE_VARIABLES_SIDEBAR.BARCODE39_VALUE_ERROR_MESSAGE.value,
    );
  } else if (errorMessages?.[name]?.mandatory) {
    errorMessage = t(
      TEMPLATE_VARIABLES_SIDEBAR.REQUIRED_ERROR_MESSAGE.key,
      TEMPLATE_VARIABLES_SIDEBAR.REQUIRED_ERROR_MESSAGE.value,
    );
  } else if (errorMessages?.[name]?.length) {
    errorMessage = t(
      TEMPLATE_VARIABLES_SIDEBAR.MAX_CHAR_ERROR_MESSAGE.key,
      TEMPLATE_VARIABLES_SIDEBAR.MAX_CHAR_ERROR_MESSAGE.value,
    );
  } else if (errorMessages?.[name]?.dateFormat) {
    errorMessage = t(
      TEMPLATE_VARIABLES_SIDEBAR.DATE_FORMAT_ERROR_MESSAGE.key,
      TEMPLATE_VARIABLES_SIDEBAR.DATE_FORMAT_ERROR_MESSAGE.value,
    );
  } else if (errorMessages?.[name]?.empty) {
    errorMessage = t(
      TEMPLATE_VARIABLES_SIDEBAR.EMPTY_ERROR_MESSAGE.key,
      TEMPLATE_VARIABLES_SIDEBAR.EMPTY_ERROR_MESSAGE.value,
    );
  }
  else if (errorMessages?.[name]?.invalidHtml) {
    errorMessage = t(
      TEMPLATE_VARIABLES_SIDEBAR.HTML_INVALID_ERROR_MESSAGE.key,
      TEMPLATE_VARIABLES_SIDEBAR.HTML_INVALID_ERROR_MESSAGE.value,
    );
  }
  else if (errorMessages?.[name]?.invalidImgId) {
    errorMessage = t(
      TEMPLATE_VARIABLES_SIDEBAR.INVALID_IMAGE_ID.key,
      TEMPLATE_VARIABLES_SIDEBAR.INVALID_IMAGE_ID.value,
    );
  }
  if (errorMessage) {
    return <div className="warning-text">{errorMessage}</div>;
  }
  return errorMessage;
};

const ChartTable = ({
  variable,
  onChangeSampleVal,
  onAddNewSampleValue,
  onDeleteSampleValue,
  isFormTable = false,
  errorMessages,
  t,
  isObject = false,
  isInteractiveDesign = false,
  isGenerateFormPage = false
}) => {
  const { value = {} } = variable;
  const { header = [], source = [] } = value;
  const [tableRef, tableKey] = useCheckTableInFocus();
  const columns = useMemo(() => {
    const actionColumn = {
      title: '',
      dataIndex: 'more',
      key: 'more',
      fixed: true,
      width: 40,
      render: (text, record, index) => {
        if (source.length > 1) {
          return (
            <div>
              <ChartColorCell index={index} deleteRow={deleteRow} t={t} addActions={false} />
            </div>
          );
        }
        return <></>;
      },
    };
    const columnList = header.map(item => {
      if (isFormTable && item === 'selected') {
        return {
          title: item,
          dataIndex: item,
          key: item,
          render: (text, record, index) => (
            <div className="checkbox-wrapper">
              <Checkbox
                checked={text === 'true' ? true : false}
                onChange={e =>
                  onChangeSampleVal(
                    { key: item, content: String(e.target.checked), index },
                    variable,
                  )
                }
              />
            </div>
          ),
        };
      }
      const key = isObject ? `${variable.key}/${variable.name}` : `${variable.key}`;
      const errorMessage =
        !isFormTable && Object.keys(errorMessages).length
          ? ErrorLabel({ errorMessages, variable : { type: 'String', name: item, key: key }, t, isObject: true })
          : null;
      const splittedLabel = isGenerateFormPage ? item?.split('.')?.join(' ') : item;
      return {
        title: (
          <ColumnHeaderErrorIcon>
            <div>{splittedLabel}</div>
            {errorMessage ? (
              <Tooltip title={errorMessage}>
                <Icon type="exclamation-circle" />
              </Tooltip>
            ) : null}
          </ColumnHeaderErrorIcon>
        ),
        dataIndex: item,
        key: item,
        render: (text, record, index) => (
          <EditableCell
            record={record}
            text={text}
            id={item}
            isPreview={true}
            updateSource={(record, key, content) =>
              onChangeSampleVal({ key, content, index }, variable)
            }
          />
        ),
      };
    });
    return isInteractiveDesign ? [...columnList] :[actionColumn, ...columnList];
  }, [header, source, errorMessages]);

  const deleteRow = index => {
    onDeleteSampleValue({ index }, variable);
  };

  return (
    <TableContainer ref={tableRef} isFormTable={isFormTable}>
      <Table
        columns={columns}
        dataSource={source}
        pagination={false}
        size="middle"
        bordered
        tableLayout="auto"
        style={{ marginTop: '10px' }}
        key={tableKey}
        scroll={{ x: 505 }}
      />
      {!isInteractiveDesign && (
        <Button
          type="link"
          onClick={() => onAddNewSampleValue(source[source.length - 1], variable)}
        >
          <Icon type="plus" />{' '}
          {t(
            COMPLEX_VARIABLES_SIDEBAR.ADD_ROW_LABEL.key,
            COMPLEX_VARIABLES_SIDEBAR.ADD_ROW_LABEL.value,
          )}
        </Button>
      )}
    </TableContainer>
  );
};

const HtmlPreview = ({ t, targetVal, varName, isHtmlExpanded, expandedVariablesList, setIsHtmlExpanded, setExpandedVariablesList, examplValuesData, setExamplValuesData, variable, isInteractiveDesign }) => {
  const editorRef = useRef(null);

  const quillContentChangeHandler = (html, delta, source, editor, variable) => {
    if (!editorRef.current || source !== 'user') return;
    const quillContents = editor.getContents()
    const converter = new QuillDeltaToHtmlConverter(quillContents.ops, {
      customCssClasses: {
        'ql-align-right': 'text-align: right;',
      },
      inlineStyles: true
    });
    const convertedHtml = converter.convert();
    const placeholder = variable.placeHolderText;
    let allValues = {
      ...examplValuesData,
      [placeholder]: {
        ...examplValuesData[placeholder],
        value: convertedHtml,
      },
    };
    setExamplValuesData(allValues);
  }
  return <RichTextEditorContainer id={`tool-${varName}`}
    isExpanded={isHtmlExpanded}
    uniqid={`tool-${varName}`}
    expandedVariablesList={expandedVariablesList}
  >
    <>
      <CustomToolBar className='custom-picker' t={t} editorRef={editorRef} setIsHtmlExpanded={setIsHtmlExpanded} isHtmlExpanded={isHtmlExpanded} varName={varName} setExpandedVariablesList={setExpandedVariablesList} expandedVariablesList={expandedVariablesList} isInteractiveDesign={isInteractiveDesign}/>
      {targetVal?.key ?
        <ReactQuill
          className='custom-picker'
          ref={editorRef}
          readOnly={isInteractiveDesign}
          modules={modules(varName)}
          defaultValue={targetVal?.value}
          onChange={(html, delta, source, editor) => { quillContentChangeHandler(html, delta, source, editor, variable) }}
        /> : null}
    </>
  </RichTextEditorContainer>
}
const NAMESPACES = ['TEMPLATE_VARIABLES_SIDEBAR', 'COMPLEX_VARIABLES_SIDEBAR'];

const VariableForm = props => {
  const { documentVariables, examplValuesData, setExamplValuesData, errorMessages,setIsHtmlExpanded ,isHtmlExpanded, isInteractiveDesign = false, isGenerateFormPage = false } = props;
  const { t } = useTranslation(NAMESPACES);

  const [enteredSample, setEnteredSample] = useState({});
  const editorRef = useRef(null);
  const [expandedVariablesList,setExpandedVariablesList] =useState([]);
  const handleClose = useCallback( // TODO : Handle list variable inside nested contract
    (removedTag, variable) => {
      const placeholder = variable.placeHolderText || variable.name;
      let allValues = {};
      if (variable?.isObject) {
        let d = examplValuesData[variable.key]?.value.forEach(val => {
          if (val.key === variable.subKey) {
            val.value.map(subVal => {
              if (subVal.name === variable.name) {
                const tags = subVal.value.filter(tag => tag !== removedTag);
                subVal.value = tags;
              }
            });
          }
        });
        allValues = {
          ...examplValuesData,
        };
      } else {
        const tags = examplValuesData[placeholder]?.value.filter(tag => tag !== removedTag);
        allValues = {
          ...examplValuesData,
          [placeholder]: {
            ...examplValuesData[placeholder],
            value: tags,
          },
        };
      }
      setExamplValuesData(allValues);
    },
    [examplValuesData],
  );

  const handleInputConfirm = useCallback(
    (e, placeholder) => {
      let allValues = {};
      if (e?.isObject) {
        let objArray = examplValuesData[e.key]?.value;
        objArray.forEach(obj => {
          if (obj.key === e.subKey) {
            obj.value.forEach(subobj => {
              if (subobj.name === placeholder) {
                subobj.value = [...subobj?.value, e.value];
              }
            });
          }
        });
        allValues = {
          ...examplValuesData,
          [e.key]: {
            ...examplValuesData[e.key],
            value: [...examplValuesData[e.key].value],
          },
        };
      } else {
        const exampleValues = examplValuesData[placeholder]?.value ? examplValuesData[placeholder]?.value : [];
        allValues = {
          ...examplValuesData,
          [placeholder]: {
            ...examplValuesData[placeholder],
            value: [...exampleValues, e?.value],
          },
        };
      }
      setExamplValuesData(allValues);
      setEnteredSample(undefined);
    },
    [examplValuesData],
  );

  const onChangeSampleVal = useCallback(
    (data, variable) => {
      const placeholder = variable?.isObject ? variable.key : variable.placeHolderText;
      let allValues = Object.assign({}, examplValuesData);
      if (variable?.isObject) {
        const split = variable.key.split('.');
        let changedValue = allValues[split[0]].value;
          const blockNameSplit = variable.parent?.split('.') || [];
          for (let i = 0; i <= blockNameSplit.length; i++) {
            if (split[i + 1]) {
              const selectedBlock = changedValue.find(obj => obj.key === blockNameSplit[i]);
              changedValue = selectedBlock.value.find(obj => obj.name === split[i + 1]).value.value;
            }
          }
        changedValue.forEach(eachBlock => {
          eachBlock.value.forEach(eachVal => {
            if (eachVal.subKey === variable.subKey && eachVal.name === variable.name) {
              if (
                eachVal.type === VARIABLE_TYPE.CHART_OBJECT ||
                eachVal.type === VARIABLE_TYPE.FORM_OBJECT
              ) {
                eachVal.value.source = eachVal.value.source.map((val, index) => {
                  if (index === data.index && typeof val[data.key] !== 'undefined') {
                    val[data.key] = data.content;
                  }
                  return val;
                });
              } else if (eachVal.type === VARIABLE_TYPE.TABLE_OBJECT) {
                eachVal.value.value = data;
              } else {
                eachVal.value = data;
              }
            }
          });
        });
        /*allValues = {
          ...examplValuesData,
          [placeholder]: {
            ...examplValuesData[placeholder],
            value: changedValue,
          },
        };*/
      } else {
        allValues = {
          ...examplValuesData,
          [placeholder]: {
            ...examplValuesData[placeholder],
            value: data,
          },
        };
      }
      // TODO : Mutation in altering examplValuesData
      setExamplValuesData(allValues);
    },
    [examplValuesData],
  );
  const quillContentChangeHandler= (html, delta, source,editor,variable)=>{
    if (!editorRef.current || source !== 'user') return;
    const quillContents=editor.getContents()
    const converter = new QuillDeltaToHtmlConverter(quillContents.ops, {customCssClasses: {
      'ql-align-right': 'text-align: right;',
    },
    inlineStyles: true});
    const convertedHtml = converter.convert();    
    const placeholder=variable.placeHolderText;
    let allValues = {
      ...examplValuesData,
      [placeholder]: {
        ...examplValuesData[placeholder],
        value: convertedHtml,
      },
    };
    setExamplValuesData(allValues);
  }
  const onDeleteSampleValue = (data, variable) => {
    const placeholder = variable?.isObject ? variable.key : variable.placeHolderText;
    let allValues = {};
    if (variable?.isObject) {
      let changedValue = examplValuesData[placeholder].value;
      changedValue.forEach(eachBlock => {
        eachBlock.value.forEach(eachVal => {
          if (eachVal.subKey === variable.subKey && eachVal.name === variable.name) {
            if (
              eachVal.type === VARIABLE_TYPE.CHART_OBJECT ||
              eachVal.type === VARIABLE_TYPE.FORM_OBJECT ||
              eachVal.type === VARIABLE_TYPE.TABLE_OBJECT
            ) {
              eachVal.value.source = eachVal.value.source.filter(
                (v, index) => index !== data.index,
              );
            }
          }
        });
      });
      allValues = {
        ...examplValuesData,
        [placeholder]: {
          ...examplValuesData[placeholder],
          value: changedValue,
        },
      };
      setExamplValuesData(allValues);
    }
  };

  const onAddNewSampleValue = (data, variable) => {
    const placeholder = variable?.isObject ? variable.key : variable.placeHolderText;
    let allValues = {};
    if (variable?.isObject) {
      let changedValue = examplValuesData[placeholder].value;
      changedValue.forEach(eachBlock => {
        eachBlock.value.forEach(eachVal => {
          if (eachVal.subKey === variable.subKey && eachVal.name === variable.name) {
            if (
              eachVal.type === VARIABLE_TYPE.CHART_OBJECT ||
              eachVal.type === VARIABLE_TYPE.FORM_OBJECT
            ) {
              eachVal.value.source = [...eachVal.value.source, deepCopy(data)];
            }
          }
        });
      });
      allValues = {
        ...examplValuesData,
        [placeholder]: {
          ...examplValuesData[placeholder],
          value: changedValue,
        },
      };
      setExamplValuesData(allValues);
    }
  };

  const getListVariable = variable => {
    let targetVal = examplValuesData[variable.placeHolderText];
    const checkboxFieldNames = variable?.extraInfo
      ?.filter(el => !targetVal?.value?.includes(el?.formOptions?.fieldName))
      .map(el => el?.formOptions?.fieldName);
    return (
      <ModalTagContainer tagSearch={true}>
        {targetVal?.value?.map(tag => {
          const tagElem = (
            <Tag
              className="edit-tag"
              key={tag}
              closable={isInteractiveDesign ? false : true}
              onClose={() => handleClose(tag, variable)}
              color="#df4c35"
              style={{ textWrap: 'wrap' }}
            >
              <span>{tag}</span>
            </Tag>
          );
          return tagElem;
        })}
        {!variable?.extraInfo?.length ? (
          <Input
            name="tagName"
            type="text"
            className="tag-input"
            value={enteredSample?.key === targetVal?.key ? enteredSample?.value : ''}
            onChange={e =>
              setEnteredSample({
                key: targetVal?.key,
                value: e.target.value,
              })
            }
            onPressEnter={e =>
              e.target.value && handleInputConfirm(enteredSample, variable.placeHolderText)
            }
            placeholder={t(
              `COMPLEX_VARIABLES_SIDEBAR:${COMPLEX_VARIABLES_SIDEBAR.ADD_NEW_SAMPLE_ACTION.key}`,
              COMPLEX_VARIABLES_SIDEBAR.ADD_NEW_SAMPLE_ACTION.value,
            )}
            autoComplete={'off'}
            disabled={isInteractiveDesign}
          />
        ) : (
          <AutoComplete
            className="tag-input"
            disabled={isInteractiveDesign || !checkboxFieldNames?.length}
            dataSource={checkboxFieldNames}
            value={enteredSample?.key === targetVal?.key ? enteredSample?.value : ''}
            onChange={value => {
              const sample = {
                key: targetVal?.key,
                value: value,
              };
              setEnteredSample(sample);
            }}
            onSelect={value => {
              const sample = {
                key: targetVal?.key,
                value: value,
              };
              handleInputConfirm(sample, variable.placeHolderText);
            }}
            filterOption={(input, option) =>
              option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            placeholder={'Select a fieldname'}
          />
        )}
      </ModalTagContainer>
    );
  };

  const getBlockListVariable = (variable, objectVal = {}) => {
    let targetVal = {};
    let objArray = examplValuesData[variable.key]?.value;
    objArray.forEach(obj => {
      if (obj.key === variable.subKey) {
        obj.value.forEach(subobj => {
          if (subobj.name === variable.name) {
            targetVal = subobj;
          }
        });
      }
    });
    const checkboxFieldNames = variable?.extraInfo
      ?.filter(el => !targetVal?.value?.includes(el?.formOptions?.fieldName))
      .map(el => el?.formOptions?.fieldName);
    return (
      <ModalTagContainer tagSearch={true}>
        {targetVal?.value.map(tag => {
          const isLongTag = tag.length > 20;
          const tagElem = (
            <Tag
              className="edit-tag"
              key={tag}
              closable={isInteractiveDesign ? false : true}
              onClose={() => handleClose(tag, variable, objectVal)}
              color="#df4c35"
            >
              <span>{isLongTag ? `${tag.slice(0, 20)}...` : tag}</span>
            </Tag>
          );
          return isLongTag ? (
            <Tooltip title={tag} key={tag}>
              {tagElem}
            </Tooltip>
          ) : (
            tagElem
          );
        })}
        {!variable?.extraInfo?.length ? (
          <Input
            name="tagName"
            type="text"
            className="tag-input"
            value={
              enteredSample?.subKey === objectVal.key && variable.key === enteredSample.key
                ? enteredSample.value
                : ''
            }
            onChange={e => {
              setEnteredSample({
                key: variable.key,
                subKey: variable.subKey,
                value: e.target.value,
                isObject: true,
              });
            }}
            onPressEnter={e => e.target.value && handleInputConfirm(enteredSample, variable.name)}
            placeholder={t(
              `COMPLEX_VARIABLES_SIDEBAR:${COMPLEX_VARIABLES_SIDEBAR.ADD_NEW_SAMPLE_ACTION.key}`,
              COMPLEX_VARIABLES_SIDEBAR.ADD_NEW_SAMPLE_ACTION.value,
            )}
            autoComplete={'off'}
            disabled={isInteractiveDesign}
          />
        ) : (
          <AutoComplete
            className="tag-input"
            disabled={isInteractiveDesign || !checkboxFieldNames?.length}
            dataSource={checkboxFieldNames}
            value={
              enteredSample?.subKey === objectVal.key && variable.key === enteredSample.key
                ? enteredSample.value
                : ''
            }
            onChange={value => {
              const sample = {
                key: variable?.key,
                subKey: variable.subKey,
                value: value,
                isObject: true,
              };
              setEnteredSample(sample);
            }}
            onSelect={value => {
              const sample = {
                key: variable?.key,
                subKey: variable.subKey,
                value: value,
                isObject: true,
              };
              handleInputConfirm(sample, variable.name);
            }}
            filterOption={(input, option) =>
              option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            placeholder={'Select a fieldname'}
          />
        )}
      </ModalTagContainer>
    );
  };

  const getDatevariable = variable => {
    let targetVal = examplValuesData[variable.placeHolderText];
    if (variable?.isObject) {
      const split = variable.key.split('.');
      let objArray = examplValuesData[split[0]]?.value;
      const blockNameSplit = variable.parent?.split('.') || [];
      for (let i = 0; i <= blockNameSplit.length; i++) {
        if (split[i + 1]) {
          const selectedBlock = objArray.find(obj => obj.key === blockNameSplit[i]);
          objArray = selectedBlock.value.find(obj => obj.name === split[i + 1]).value.value;
        }
      }
      objArray.forEach(obj => {
        if (obj.key === variable.subKey) {
          obj.value.map(subVal => {
            if (subVal.name === variable.name) {
              targetVal = subVal;
            }
          });
        }
      });
    }
    return (
      <DatePicker
        value={moment(targetVal?.value, 'DD-MM-YYYY')}
        format="DD-MM-YYYY"
        placeholder={t(
          `COMPLEX_VARIABLES_SIDEBAR:${COMPLEX_VARIABLES_SIDEBAR.SELECT_DATE_PLACEHOLDER.key}`,
          COMPLEX_VARIABLES_SIDEBAR.SELECT_DATE_PLACEHOLDER.value,
        )}
        onChange={(e, dateString) => onChangeSampleVal(dateString, variable)}
        allowClear={false}
        showTime={false}
        dropdownClassName={isGenerateFormPage ? '' : "complexDateVar"}
        disabled={ isInteractiveDesign }
      />
    );
  };

  const getStringVariable = variable => {
    let targetVal = examplValuesData[variable.placeHolderText];
    if (variable?.isObject) {
      const split = variable.key.split('.');
      let objArray = examplValuesData[split[0]]?.value;
      const blockNameSplit = variable.parent?.split('.') || [];
      for (let i = 0; i < blockNameSplit.length; i++) {
        if (split[i + 1]) {
          const selectedBlock = objArray.find(obj => obj.key === blockNameSplit[i]);
          objArray = selectedBlock?.value?.find(obj => obj.name === split[i + 1])?.value?.value;
        }
      }
      if (!objArray) {
        console.log({ variable }); // TODO : Debugging crash
      }
      if (objArray) {
        objArray.forEach(obj => {
          if (obj.key === variable.subKey) {
            obj.value && obj.value.forEach(subVal => {
              if (subVal.name === variable.name) {
                targetVal = subVal;
              }
            });
          }
        });
      }
    }
    if (variable?.listInfo) {
      const radioButtonNames = variable?.listInfo
        ?.filter(el => !targetVal?.value?.includes(el?.formOptions?.fieldName))
        .map(el => el?.formOptions?.fieldName);
      return (
        <Select
          showSearch
          style={{ width: '100%' }}
          value={targetVal?.value}
          onChange={value => {
            onChangeSampleVal(value, variable);
          }}
          filterOption={(input, option) =>
            option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
          placeholder={'Select a fieldname'}
          disabled={isInteractiveDesign}
        >
          {radioButtonNames?.map(value => (
            <Option value={value}>{value}</Option>
          ))}
        </Select>
      );
    } else if (variable?.numberInfo) {
      return (
        <InputNumber
          value={targetVal?.value ? Number(targetVal?.value) : targetVal?.value}
          onChange={value => {
            let newValue = value ? String(value) : value;
            if (newValue === null) {
              newValue = '';
            }
            onChangeSampleVal(newValue, variable);
          }}
          disabled={isInteractiveDesign}
        ></InputNumber>
      );
    }

    return (
      <Input
        value={targetVal?.value}
        onChange={e => {
          onChangeSampleVal(e.target.value, variable);
        }}
        disabled={isInteractiveDesign}
      />
    );
  };

  const getHtmlVariable = variable => {
    let targetVal = examplValuesData[variable.placeHolderText];
    let varName=targetVal?.key;
    return (
      <HtmlPreview t={t} targetVal={targetVal} varName={varName} isHtmlExpanded={isHtmlExpanded} setIsHtmlExpanded={setIsHtmlExpanded} setExpandedVariablesList={setExpandedVariablesList} expandedVariablesList={expandedVariablesList} examplValuesData={examplValuesData} setExamplValuesData={setExamplValuesData} variable={variable} isInteractiveDesign={isInteractiveDesign} />
    );
  }; 

  const getBooleanVariable = variable => {
    let targetVal = examplValuesData[variable.placeHolderText];
    if (variable?.isObject) {
      const split = variable.key.split('.');
      let objArray = examplValuesData[split[0]]?.value;
      const blockNameSplit = variable.parent?.split('.') || [];
      for (let i = 0; i <= blockNameSplit.length; i++) {
        if (split[i + 1]) {
          const selectedBlock = objArray.find(obj => obj.key === blockNameSplit[i]);
          objArray = selectedBlock.value.find(obj => obj.name === split[i + 1]).value.value;
        }
      }
      objArray.forEach(obj => {
        if (obj.key === variable.subKey) {
          obj.value.map(subVal => {
            if (subVal.name === variable.name) {
              targetVal = subVal;
            }
          });
        }
      });
    }
    return (
      <Switch
        checked={targetVal?.value}
        checkedChildren="YES"
        unCheckedChildren="NO"
        onChange={e => {
          onChangeSampleVal(e, variable);
        }}
      />
    );
  };

  const getChartVariable = (variable, isObject = false) => {
    return (
      <ChartTable
        variable={variable}
        onChangeSampleVal={onChangeSampleVal}
        onAddNewSampleValue={onAddNewSampleValue}
        onDeleteSampleValue={onDeleteSampleValue}
        errorMessages={errorMessages}
        t={t}
        isObject={isObject}
        isInteractiveDesign={isInteractiveDesign}
        isGenerateFormPage={isGenerateFormPage}
      />
    );
  };

  const getTableVariable = (variable, columnData, nested = false) => {
    return (
        <ChangeVariableValue
          key={variable.placeHolderText}
          onChangeSampleVal={e => onChangeSampleVal(e, variable)}
          columnData={columnData}
          getBlockListVariable={getBlockListVariable}
          getBlockDatevariable={getDatevariable}
          getBlockStringVariable={getStringVariable}
          getBlockBooleanVariable={getBooleanVariable}
          getChartVariable={getChartVariable}
          getTableVariable={getTableVariable}
          errorMessages={errorMessages}
          nested={nested}
          isArray={variable.isArray}
          isInteractiveDesign={isInteractiveDesign}
          isGenerateFormPage={isGenerateFormPage}
        />
    )
  }

  const getFormVariable = variable => {
    return (
      <ChartTable
        variable={variable}
        onChangeSampleVal={onChangeSampleVal}
        onAddNewSampleValue={onAddNewSampleValue}
        onDeleteSampleValue={onDeleteSampleValue}
        isFormTable={true}
        t={t}
        isInteractiveDesign={isInteractiveDesign}
        isGenerateFormPage={isGenerateFormPage}
      />
    );
  };

  return (
    <>
      {documentVariables && documentVariables.length > 0 ? (
        <ListGridContainer isInteractiveDesign={isInteractiveDesign}>
          {documentVariables.map((variable, index) => {
          const splittedLabel = isGenerateFormPage
            ? variable?.placeHolderText?.split('.')?.join(' ')
            : variable.placeHolderText;
            return <>
              <VariableDataContainer isObject={VARIABLE_OBJECT_TYPES.includes(variable.type)}>
                <VariableDetail
                  isVarName={1}
                  isList={variable.type === 'ListString' || variable.type === 'listOfStrings'}
                  isHtml={variable.type===VARIABLE_TYPE.HTML}
                >
                  {(variable.type === 'ListString' ||
                    variable.type === 'ListDate' ||
                    variable.type === 'listOfStrings') && (
                    <Tooltip
                      placement="top"
                      title={t(
                        TEMPLATE_VARIABLES_SIDEBAR.MULTI_VALUE_VARIABLE_TOOLTIP.key,
                        TEMPLATE_VARIABLES_SIDEBAR.MULTI_VALUE_VARIABLE_TOOLTIP.value,
                      )}
                    >
                      <div className="variable-type">L</div>
                    </Tooltip>
                  )}
                  {variable.type === 'Date' && (
                    <Tooltip
                      placement="top"
                      title={t(
                        TEMPLATE_VARIABLES_SIDEBAR.DATE_VARIABLE_TOOLTIP.key,
                        TEMPLATE_VARIABLES_SIDEBAR.DATE_VARIABLE_TOOLTIP.value,
                      )}
                    >
                      <div className="variable-type">D</div>
                    </Tooltip>
                  )}
                  {variable.type === 'table' && (
                    <Tooltip
                      placement="top"
                      title={t(
                        TEMPLATE_VARIABLES_SIDEBAR.TABLE_VARIABLE_TOOLTIP.key,
                        TEMPLATE_VARIABLES_SIDEBAR.TABLE_VARIABLE_TOOLTIP.value,
                      )}
                    >
                      <div className="variable-type">T</div>
                    </Tooltip>
                  )}
                  {variable.type === 'boolean' && (
                    <Tooltip
                      placement="top"
                      title={t(
                        TEMPLATE_VARIABLES_SIDEBAR.BOOLEAN_VARIABLE_TOOLTIP.key,
                        TEMPLATE_VARIABLES_SIDEBAR.BOOLEAN_VARIABLE_TOOLTIP.value,
                      )}
                    >
                      <div className="variable-type">B</div>
                    </Tooltip>
                  )}
                  {variable.type === 'String' && (
                    <Tooltip
                      placement="top"
                      title={t(
                        TEMPLATE_VARIABLES_SIDEBAR.STRING_VARIABLE_TOOLTIP.key,
                        TEMPLATE_VARIABLES_SIDEBAR.STRING_VARIABLE_TOOLTIP.value,
                      )}
                    >
                      <div className="variable-type">S</div>
                    </Tooltip>
                  )}
                   {variable.type === VARIABLE_TYPE.HTML && (
                    <Tooltip
                      placement="top"
                      title={t(
                        TEMPLATE_VARIABLES_SIDEBAR.HTML_VARIABLE_TOOLTIP.key,
                        TEMPLATE_VARIABLES_SIDEBAR.HTML_VARIABLE_TOOLTIP.value,
                      )}>
                      <div className="variable-type">H</div>
                    </Tooltip>
                  )}
                  {variable.type === 'object' && (
                    <Tooltip
                      placement="top"
                      title={t(
                        TEMPLATE_VARIABLES_SIDEBAR.REPEAT_VARIABLE_TOOLTIP.key,
                        TEMPLATE_VARIABLES_SIDEBAR.REPEAT_VARIABLE_TOOLTIP.value,
                      )}
                    >
                      <div className="variable-type">R</div>
                    </Tooltip>
                  )}
                  {variable.type === VARIABLE_TYPE.TABLE_OBJECT && (
                    <Tooltip
                      placement="top"
                      title={t(
                        TEMPLATE_VARIABLES_SIDEBAR.TABLE_VARIABLE_TOOLTIP.key,
                        TEMPLATE_VARIABLES_SIDEBAR.TABLE_VARIABLE_TOOLTIP.value,
                      )}
                    >
                      <div className="variable-type">T</div>
                    </Tooltip>
                  )}
                  {variable.type === VARIABLE_TYPE.CHART_OBJECT && (
                    <Tooltip
                      placement="top"
                      title={t(
                        TEMPLATE_VARIABLES_SIDEBAR.CHART_VARIABLE_TOOLTIP.key,
                        TEMPLATE_VARIABLES_SIDEBAR.CHART_VARIABLE_TOOLTIP.value,
                      )}
                    >
                      <div className="variable-type">C</div>
                    </Tooltip>
                  )}
                  {variable.type === VARIABLE_TYPE.FORM_OBJECT && (
                    <Tooltip
                      placement="top"
                      title={t(
                        TEMPLATE_VARIABLES_SIDEBAR.FORM_VARIABLE_TOOLTIP.key,
                        TEMPLATE_VARIABLES_SIDEBAR.FORM_VARIABLE_TOOLTIP.value,
                      )}
                    >
                      <div className="variable-type">F</div>
                    </Tooltip>
                  )}
                  <p style={{ textTransform: 'none' }}>{splittedLabel} {variable?.isMandatory ? <span style={{color: 'red', fontSize: '16px'}}>*</span>: null}</p>
                </VariableDetail>
                <VariableDetail
                  id={`tool-${variable.name}`}
                  expandedVariablesList={expandedVariablesList}
                  uniqid={`tool-${variable.name}`}
                  isHtmlBlockExpanded={isHtmlExpanded}
                  isHtml={variable.type===VARIABLE_TYPE.HTML}
                  isBlock={
                    VARIABLE_OBJECT_TYPES.includes(variable.type) ||
                    variable.type === VARIABLE_TYPE.FORM_OBJECT ||
                    variable.type === VARIABLE_TYPE.TABLE_OBJECT
                  }
                >
                  {variable.type === 'ListString' || variable.type === 'listOfStrings' ? (
                    getListVariable(variable)
                  ) : variable.type === 'Date' ? (
                    getDatevariable(variable)
                  ) : variable.type === VARIABLE_TYPE.CHART_OBJECT &&
                    Object.keys(examplValuesData).length ? (
                    getChartVariable(examplValuesData[variable.name]?.value?.[0]?.value?.[0] || {})
                  ) : variable.type === VARIABLE_TYPE.FORM_OBJECT &&
                    Object.keys(examplValuesData).length ? (
                    getFormVariable(examplValuesData[variable.name]?.value?.[0]?.value?.[0] || [])
                  ) : variable.type === VARIABLE_TYPE.TABLE_OBJECT &&
                    Object.keys(examplValuesData).length ? (
                     getTableVariable(variable || {}, examplValuesData[variable.name])
                  ) : VARIABLE_OBJECT_TYPES.includes(variable.type) ? (
                    <ChangeVariableValue
                      onChangeSampleVal={e => onChangeSampleVal(e, variable)}
                      columnData={examplValuesData[variable.name]}
                      getBlockListVariable={getBlockListVariable}
                      getBlockDatevariable={getDatevariable}
                      getBlockStringVariable={getStringVariable}
                      getBlockBooleanVariable={getBooleanVariable}
                      getChartVariable={getChartVariable}
                      getTableVariable={getTableVariable}
                      errorMessages={errorMessages}
                      isGenerateFormPage={isGenerateFormPage}
                    />
                  ) : variable.type === 'boolean' ? (
                    getBooleanVariable(variable)
                  ) : variable.type === VARIABLE_TYPE.HTML && !variable.id ? (
                    getHtmlVariable(variable)): (
                    getStringVariable(variable)
                  )}
                </VariableDetail>
              </VariableDataContainer>
              {Object.keys(errorMessages).length &&
              [...VARIABLE_PRIMITIVE_TYPES, VARIABLE_TYPE.LIST_OF_STRINGS, VARIABLE_TYPE.HTML].includes(
                variable.type,
              ) ? (
                <ErrorLabel errorMessages={errorMessages} variable={variable} t={t} />
              ) : null}
            </>
          })}
        </ListGridContainer>
      ) : (
        <EmptyListContainer>
          <EmptyListImage>
            <img src={EmptyList} alt="empty-list" />
          </EmptyListImage>
          <EmptyListHelpText>
            {t(
              TEMPLATE_VARIABLES_SIDEBAR.NO_VARIABLES_IN_TEMPLATE_DESCRIPTION.key,
              TEMPLATE_VARIABLES_SIDEBAR.NO_VARIABLES_IN_TEMPLATE_DESCRIPTION.value,
            )}
          </EmptyListHelpText>
        </EmptyListContainer>
      )}
    </>
  );
};

export default VariableForm;
